import { useRouter } from 'next/router'
import { PropsWithChildren, useMemo } from 'react'
import { MdAddCircle } from 'react-icons/md'

import { ModalType, useModal } from 'src/providers/ModalProvider'
import { useUser } from 'src/providers/UserProvider'

import { ButtonRebrand } from 'src/components/atoms/ButtonRebrand'
import { Card } from 'src/components/atoms/Card'
import { Heading1 } from 'src/components/atoms/Typography/Headers'
import {
  Action,
  ActionsDropdown
} from 'src/components/molecules/ActionsDropdown'
import { Tabs } from 'src/components/molecules/Tabs'
import { TabData } from 'src/components/molecules/Tabs/Tabs'

import {
  createAccountPath,
  updateAccountDetailsPath,
  viewAccountUsersPath,
  viewProfilePath,
  viewAccountDetailPath,
  viewAccountProfilePath,
  updateAccountProfilePath
} from 'src/config/paths'
import { env } from 'src/env/client.mjs'
import { Stages } from 'src/types/stages'
import { isHidden } from 'src/utils/account'

export enum AccountNavTab {
  Details = 'details',
  Profile = 'profile',
  Users = 'users',
  PlantingExperience = 'plantingExperience'
}

type AccountNavProps = PropsWithChildren<{
  activeTab: AccountNavTab
}>

export const AccountNav = ({ activeTab, children }: AccountNavProps) => {
  const router = useRouter()
  const { user, isAccountOwner } = useUser()
  const { openModal } = useModal()

  const isProduction = env.NEXT_PUBLIC_APP_STAGE === Stages.PRODUCTION

  const actions: Action[] = isProduction
    ? []
    : [
        {
          text: 'Add a new account',
          href: createAccountPath
        }
      ]

  if (activeTab === AccountNavTab.Details) {
    actions.unshift({
      text: 'Edit account details',
      href: updateAccountDetailsPath
    })
  } else if (activeTab === AccountNavTab.Profile) {
    actions.unshift({
      text: 'Edit account profile',
      href: updateAccountProfilePath
    })
  }
  if (user?.Account?.Uri && !isHidden(user?.Account)) {
    actions.push({
      text: 'View public profile',
      href: viewProfilePath({
        routeParams: { uri: user?.Account?.Uri! }
      })
    })
  }

  const tabs = useMemo(() => {
    const availableTabs: TabData[] = [
      {
        label: 'Details',
        value: AccountNavTab.Details,
        path: viewAccountDetailPath
      },
      {
        label: 'Profile',
        value: AccountNavTab.Profile,
        path: viewAccountProfilePath
      },

      {
        label: 'Users',
        value: AccountNavTab.Users,
        path: viewAccountUsersPath
      }
    ]

    return availableTabs
  }, [])

  const handleTabChange = (tabValue: string) => {
    if (tabValue === activeTab) {
      return null
    }

    const tab = tabs.find(t => t.value === tabValue)

    if (tab?.path) {
      router.push(tab.path)
    }
  }

  return (
    <>
      <Heading1 className='mb-4'>Account</Heading1>
      <Card size='small' corner='square' className='mb-4'>
        <div className='flex flex-col-reverse md:flex-row items-center gap-5 md:px-5'>
          <Tabs
            activeTab={activeTab}
            ariaLabel='Account navigation'
            onChange={handleTabChange}
            tabs={tabs}
          />
          <div className='place-self-end'>
            {activeTab === AccountNavTab.Users && isAccountOwner ? (
              <ButtonRebrand
                variant='outline'
                onClick={() =>
                  openModal(ModalType.InviteAccountUser, {
                    accountId: user?.ActiveAccountId
                  })
                }
              >
                Invite new member <MdAddCircle className='ml-2' />
              </ButtonRebrand>
            ) : (
              !!actions.length && (
                <ActionsDropdown
                  actions={actions}
                  ButtonComponent={ButtonRebrand}
                  buttonComponentProps={{ variant: 'outline' }}
                >
                  Actions
                </ActionsDropdown>
              )
            )}
          </div>
        </div>
        <div className='md:px-5'>{children}</div>
      </Card>
    </>
  )
}
